import { Button, Form, Header, Icon, Segment } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { Bounce, ToastContainer, toast } from 'react-toastify';
import { PROTOCOLE_IMPAYE_ORMC, PROTOCOLE_IMPAYE_PES_ASAP } from '@/constants/regie';
import { RegieContext } from '@/contexts';
import { useListeSearchSort } from '@/hooks';
import {
  getFacturesImpayees,
  getFacturesResteAPayer,
  postEditionImpayes,
  putCycle,
} from '@/routes';
import type { CycleType, FactureType } from '@/types';
import FacturesImpayeesListe from './FacturesImpayeesListe';
import ModalConfirmTransfert from './ModalConfirmTransfert';
import Resume from './Resume';
import Seuil from './Seuil';
import TransfertImpayesCycles from './TransfertImpayesCycles';
import Type from './Type';

type TransfertImpayesProps = {
  cycles: CycleType[];
  onRedirectToSelection: (resetCycles?: boolean) => void;
  onCycleUpdate: (updatedData: Partial<CycleType>) => void;
};

const TransfertImpayes = (props: TransfertImpayesProps) => {
  const { cycles, onRedirectToSelection, onCycleUpdate } = props;

  const [seuilUtilisateur, setSeuilUtilisateur] = React.useState<number | null>();
  const [type, setType] = React.useState<'facture' | 'payeur'>('facture');
  const [totalReste, setTotalReste] = React.useState<number>(null);
  const [loading, setLoading] = React.useState<boolean | 'transfert'>(false);
  const [transfertModalOpen, setTransfertModalOpen] = React.useState<
    boolean | 'PES' | 'ORMC' | 'titre'
  >(false);

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [regies, selectedRegie],
  );

  /** Seul les cycles qui n'ont pas été validés par
   le poste comptable sont eligibles pour le transfert */
  const cyclesRestant = React.useMemo(
    () => _.filter(cycles, (cycle) => _.isEmpty(cycle.dateValidationPosteComptable)),
    [cycles],
  );

  /** Seuil saisi, sinon celui de la régie. `??` et non `||` : un seuil saisi à 0
   doit l'emporter sur celui de la régie */
  const seuil = seuilUtilisateur ?? regie?.seuilRecouvrement ?? 0;

  const getFactureImpayeeRoute = React.useMemo(
    () =>
      getFacturesImpayees({
        cycles: _.map(cyclesRestant, 'identifiant'),
        typeTransfert: type,
        seuil,
      }),
    [seuil, type, cyclesRestant],
  );

  const {
    items,
    loading: loadingFactures,
    total,
    setPage,
    setItems,
  } = useListeSearchSort({
    active: !_.isEmpty(cyclesRestant),
    getItems: getFactureImpayeeRoute,
  });

  const getResteAPayer = React.useCallback(async () => {
    setTotalReste(null);

    if (!_.isEmpty(cyclesRestant)) {
      const response = (await request.get(
        getFacturesResteAPayer({
          cycles: _.map(cyclesRestant, 'identifiant'),
          typeTransfert: type,
          seuil,
        }),
      )) as { totalResteAPayer: number };

      setTotalReste(response.totalResteAPayer);
    }
  }, [cyclesRestant, type, seuil]);

  const handleOrdonnateurValidate = React.useCallback(
    (identifiantCycle) => () => {
      setLoading(true);
      toast.dismiss({ containerId: 'transfert' });

      const cycleToUpdate = _.find(cycles, { identifiant: identifiantCycle });

      request
        .put(putCycle(identifiantCycle), {
          ...cycleToUpdate,
          validationOrdonnateur: !cycleToUpdate.validationOrdonnateur,
          // Si on est en ORMC, et qu'on décoche la validation du titre global,
          // on vide la date de transfert des impayés aussi
          ...(regie?.protocoleImpaye === PROTOCOLE_IMPAYE_ORMC &&
            cycleToUpdate.validationOrdonnateur && {
              dateTransfertImpaye: null,
            }),
        })
        .then((updatedCycle: CycleType) => {
          onCycleUpdate({
            identifiant: identifiantCycle,
            validationOrdonnateur: updatedCycle.validationOrdonnateur,
            dateTransfertImpaye: updatedCycle.dateTransfertImpaye,
          });

          // Si ORMC et qu'on vient de valider le titre global, on toast
          if (
            regie?.protocoleImpaye === PROTOCOLE_IMPAYE_ORMC &&
            updatedCycle.validationOrdonnateur
          ) {
            toast(
              <Header as="h5" style={{ margin: 0, padding: 0 }}>
                Le transfert des impayés
                <br />
                est désormais possible
              </Header>,
              {
                type: 'info',
                icon: <Icon name="CornerRightDown" iconSet="Lucide" />,
                containerId: 'transfert',
                style: {
                  margin: 0,
                  width: 250,
                  direction: 'rtl',
                  textAlign: 'left',
                },
                closeButton: false,
              },
            );
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [cycles, onCycleUpdate, regie?.protocoleImpaye],
  );

  const handlePosteComptableValidate = React.useCallback(
    (identifiantCycle) => () => {
      setLoading(true);

      const cycleToUpdate = _.find(cycles, { identifiant: identifiantCycle });

      request
        .put(putCycle(identifiantCycle), {
          ...cycleToUpdate,
          dateValidationPosteComptable: !cycleToUpdate.dateValidationPosteComptable
            ? dayjs().format('YYYY-MM-DD HH:mm:ss')
            : null,
        })
        .then((updatedCycle: CycleType) => {
          onCycleUpdate({
            identifiant: identifiantCycle,
            dateValidationPosteComptable: updatedCycle.dateValidationPosteComptable,
          });
          setItems([]);
          setPage(1);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [cycles, onCycleUpdate, setItems, setPage],
  );

  const handleTransfertClose = React.useCallback(() => {
    setTransfertModalOpen(false);
  }, []);

  const handleTransfertPES = React.useCallback(() => {
    setTransfertModalOpen('PES');
  }, []);

  const handleTransfertTitre = React.useCallback(() => {
    setTransfertModalOpen('titre');
  }, []);

  const handleTransfertORMC = React.useCallback(() => {
    setTransfertModalOpen('ORMC');
  }, []);

  /** Historise en PDF la liste des impayés transférés de chaque cycle (REG-904).
   * Un échec n'annule pas le transfert déjà enregistré. */
  const historiserListeImpayes = React.useCallback(async () => {
    const results = await Promise.allSettled(
      _.map(cyclesRestant, (cycle) =>
        request.post(postEditionImpayes(cycle.identifiant), {
          typeTransfert: type,
          seuil,
        }),
      ),
    );

    _.forEach(results, (result) => {
      if (result.status === 'fulfilled') {
        const { identifiant, identifiantDocumentImpayesPdf } = result.value as CycleType;
        onCycleUpdate({ identifiant, identifiantDocumentImpayesPdf });
      }
    });

    if (_.some(results, { status: 'rejected' })) {
      toast.error("Le transfert est enregistré, mais l'édition de la liste des impayés a échoué.");
    }
  }, [cyclesRestant, type, seuil, onCycleUpdate]);

  const handleTransfertConfirm = React.useCallback(() => {
    setLoading('transfert');

    const promises = [];

    _.map(cycles, (cycle) => {
      promises.push(
        request.put(putCycle(cycle.identifiant), {
          ...cycle,
          ...(transfertModalOpen === 'titre'
            ? { dateTransfertTitre: dayjs().format('YYYY-MM-DD HH:mm:ss') }
            : { dateTransfertImpaye: dayjs().format('YYYY-MM-DD HH:mm:ss') }),
        }),
      );
    });

    Promise.all(promises)
      .then(async (responses) => {
        _.map(responses, (updatedCycle) => {
          onCycleUpdate({
            identifiant: updatedCycle.identifiant,
            ...(transfertModalOpen === 'titre'
              ? { dateTransfertTitre: updatedCycle.dateTransfertTitre }
              : { dateTransfertImpaye: updatedCycle.dateTransfertImpaye }),
          });
        });

        // Le transfert du titre global ORMC n'est pas un transfert d'impayés : pas d'édition
        if (transfertModalOpen !== 'titre') {
          await historiserListeImpayes();
        }
      })
      .finally(() => {
        setLoading(false);
        onRedirectToSelection(false);
      });
  }, [cycles, onCycleUpdate, onRedirectToSelection, transfertModalOpen, historiserListeImpayes]);

  const handleTypeChange = React.useCallback(
    (e, { value }) => {
      setItems([]);
      setPage(1);
      setType(value);
    },
    [setItems, setPage],
  );

  const handleSeuilChange = React.useCallback(
    (e, value) => {
      setItems([]);
      setPage(1);
      setSeuilUtilisateur(value);
    },
    [setItems, setPage],
  );

  React.useEffect(() => {
    getResteAPayer();
  }, [getResteAPayer]);

  return (
    <Form>
      <Header
        as="h2"
        style={{
          margin: '0 0.5em 0 1em',
          display: 'flex',
        }}
      >
        <Header.Content style={{ flex: 1 }}>Transferts</Header.Content>
        <Button
          content="Retourner à la liste"
          style={{ width: 250 }}
          disabled={loadingFactures}
          onClick={() => onRedirectToSelection()}
        />
      </Header>
      <Segment style={{ margin: '1em', padding: '1.5em' }}>
        <TransfertImpayesCycles
          cycles={cycles}
          protocole={regie?.protocoleImpaye}
          loading={!_.isEmpty(cyclesRestant) && (loading !== false || loadingFactures)}
          onOrdonnateurValidate={handleOrdonnateurValidate}
          onPosteComptableValidate={handlePosteComptableValidate}
        />
      </Segment>
      <div
        style={{
          margin: '1em',
          paddingRight: '2em',
          display: 'flex',
          flexDirection: 'row',
          columnGap: '1em',
        }}
      >
        <Segment
          style={{
            padding: '1.5em',
            margin: 0,
            flex: 2,
          }}
        >
          <Type
            header="Type de transfert"
            subheader="Choisissez comment regrouper le transfert."
            type={type}
            onTypeChange={handleTypeChange}
            disabled={_.isEmpty(cyclesRestant) ? true : 'payeur'}
          />
        </Segment>
        <Segment
          style={{
            padding: '1.5em',
            margin: 0,
            flex: 2,
          }}
        >
          <Seuil
            value={seuil}
            onSeuilChange={handleSeuilChange}
            disabled={_.isEmpty(cyclesRestant)}
            header="Seuil de recouvrement"
          />
        </Segment>
        {!_.isEmpty(cyclesRestant) && (
          <Segment
            style={{
              padding: '1.5em',
              margin: 0,
              backgroundColor: 'rgba(235, 242, 250, 0.5)',
              minWidth: 200,
            }}
            color="blue"
          >
            <Resume
              header="À transférer"
              loading={loadingFactures}
              total={total}
              totalReste={totalReste}
            />
          </Segment>
        )}
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            minWidth: 250,
          }}
        >
          <ToastContainer
            autoClose={10000}
            limit={1}
            closeOnClick
            pauseOnHover={false}
            transition={Bounce}
            containerId="transfert"
            style={{
              position: 'relative',
              right: 0,
              top: 0,
            }}
          />
          <Button
            size="large"
            content={
              // si ORMC + pas généré le titre + pas transféré
              regie.protocoleImpaye === PROTOCOLE_IMPAYE_ORMC &&
              !_.isEmpty(cyclesRestant) &&
              !_.head(cyclesRestant)?.validationOrdonnateur
                ? 'Transférer le titre global'
                : 'Transférer les impayés'
            }
            positive
            style={{ width: 250, height: 70, margin: 0 }}
            disabled={
              loading !== false ||
              loadingFactures ||
              _.isNil(totalReste) ||
              totalReste <= 0 ||
              // Si ORMC, et titre transféré mais pas encore validé
              // on doit pas pouvoir transférer les impayés
              (regie.protocoleImpaye === PROTOCOLE_IMPAYE_ORMC &&
                !_.isNil(_.head(cyclesRestant)?.dateTransfertTitre) &&
                !_.head(cyclesRestant)?.validationOrdonnateur) ||
              (regie.protocoleImpaye === PROTOCOLE_IMPAYE_PES_ASAP &&
                _.head(cyclesRestant)?.validationOrdonnateur)
            }
            loading={loading === 'transfert'}
            onClick={
              regie.protocoleImpaye === PROTOCOLE_IMPAYE_ORMC
                ? _.isNil(_.head(cyclesRestant)?.dateTransfertTitre)
                  ? handleTransfertTitre
                  : handleTransfertORMC
                : handleTransfertPES
            }
          />
          {transfertModalOpen !== false && (
            <ModalConfirmTransfert
              loading={loading === 'transfert'}
              type={transfertModalOpen as 'PES' | 'ORMC' | 'titre'}
              total={total}
              totalReste={totalReste}
              onCancel={handleTransfertClose}
              onConfirm={handleTransfertConfirm}
            />
          )}
        </div>
      </div>
      <Segment basic>
        <Segment style={{ padding: 0, margin: 0 }}>
          <FacturesImpayeesListe
            active={!_.isEmpty(cyclesRestant)}
            items={items as FactureType[]}
            loading={loadingFactures}
            total={total}
            setPage={setPage}
          />
        </Segment>
      </Segment>
    </Form>
  );
};

export default TransfertImpayes;
